import React, { useEffect, useState } from 'react';
import { Sidebar } from 'primereact/sidebar';
import BurgerButton from './BurgerButton';
import { Button } from 'primereact/button';
import IconClose from '../../images/IconClose';
import IconLogout from '../../images/IconLogout';
import Logo from '../../images/Logo.jsx';
import useCurrentUser from '../../../hooks/useCurrentUser.js';
import { InputSwitch } from 'primereact/inputswitch';
import { useDispatch, useSelector } from 'react-redux';
import { initializeSwitches, toggleSwitch } from '../../../store/organizationSwitchSlice';
import { Accordion, AccordionTab } from 'primereact/accordion';
import '../../../resources/styles/menu-list.scss';
import { menuList } from './menuList.jsx';
import { filterMenuByPermissions } from './menuPermissions.js';
import { Link, useNavigate } from 'react-router-dom';
import { getInitials } from '../../../utils/getInitials.js';

const MenuSidebar = () => {
    const [visible, setVisible] = useState(false);
    const { user, permissions: userPermissions } = useCurrentUser();
    const dispatch = useDispatch();
    const orgSwitches = useSelector((state) => state.organizationSwitch);
    const navigate = useNavigate();

    const filteredMenu = filterMenuByPermissions(menuList, userPermissions);

    useEffect(() => {
        if (user && user.id) {
            dispatch(initializeSwitches({ userId: user.id, organizations: user.organizations }));
        }
    }, [dispatch, user]);

    const initials = getInitials(user.name);

    return (
        <>
            <BurgerButton onClick={() => setVisible(true)} />

            <Sidebar
                visible={visible}
                onHide={() => setVisible(false)}
                modal={false}
                dismissable={true}
                className="!w-[90%] !p-0 bg-white rounded-tr-4xl rounded-br-4xl bg-[url('/src/resources/images/bg-menu.png')] bg-no-repeat bg-cover text-white"
                baseZIndex={1000}
                content={({ hide }) => (
                    <div className='min-h-screen flex relative'>
                        <div className='flex flex-col h-full w-full px-[30px] py-[20px]'>
                            <div className='flex items-center justify-between flex-shrink-0 w-full mb-6'>
                                <div className='cursor-pointer' onClick={() => navigate('/')}>
                                    <Logo className='w-[80px] fill-current text-theme1-white' />
                                </div>
                                <Button
                                    type='button'
                                    onClick={(e) => hide(e)}
                                    icon='pi pi-times'
                                    className='h-2rem w-2rem border-none bg-transparent focus:outline-none focus:ring-0'
                                >
                                    <IconClose className='fill-white w-[26px] h-[26px]' />
                                </Button>
                            </div>
                            <div className='overflow-y-auto mb-4'>
                                <Accordion className='menu-list menu-list-organizations' multiple activeIndex={[]}>
                                    <AccordionTab
                                        key='organizations'
                                        header={
                                            <span className='flex items-center gap-3 pb-1'>
                                                <div className='text-[22px] font-bold'>Display Data</div>
                                            </span>
                                        }
                                        onClick={(e) => {
                                            e.stopPropagation();
                                        }}
                                    >
                                        <div
                                            key={`private-organizations-${user?.id}`}
                                            className={
                                                'flex items-center gap-3 border-b border-theme1-tanTransparent pb-2 mb-2'
                                            }
                                        >
                                            <div className='rounded-full w-[42px] h-[42px] overflow-hidden'>
                                                <div className='w-full h-full bg-theme1-pale flex items-center justify-center'>
                                                    <div className='text-theme1-primary font-bold text-base'>
                                                        {getInitials('Private')}
                                                    </div>
                                                </div>
                                            </div>
                                            <span className='text-lg font-bold text-white'>Private</span>
                                            <InputSwitch
                                                checked={orgSwitches[user.id]?.private || false}
                                                onChange={() =>
                                                    dispatch(
                                                        toggleSwitch({
                                                            userId: user.id,
                                                            togglePrivate: !orgSwitches[user.id]?.private
                                                        })
                                                    )
                                                }
                                                className='ml-auto'
                                            />
                                        </div>
                                        <div className='flex flex-col gap-4 mt-2 pb-5'>
                                            {user.organizations.map((org) => (
                                                <div key={org.id} className='flex items-center gap-3 justify-between'>
                                                    <div className='rounded-full w-[42px] h-[42px] overflow-hidden'>
                                                        <div className='w-full h-full bg-theme1-pale flex items-center justify-center'>
                                                            <div className='text-theme1-primary font-bold text-base'>
                                                                {getInitials(org.name)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <span className='text-lg font-bold text-white'>{org.name}</span>
                                                    <InputSwitch
                                                        checked={orgSwitches[user.id]?.organizations[org.id] || false}
                                                        onChange={() =>
                                                            dispatch(toggleSwitch({ userId: user.id, orgId: org.id }))
                                                        }
                                                        className='ml-auto'
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </AccordionTab>
                                    {/* Add other menu items here */}
                                </Accordion>
                                <Accordion className='menu-list' multiple activeIndex={[]}>
                                    {filteredMenu.map((category) => (
                                        <AccordionTab
                                            key={category.category}
                                            header={
                                                <span className='flex items-center gap-3 w-full'>
                                                    {category.icon}
                                                    <span>{category.category}</span>
                                                </span>
                                            }
                                            onClick={(e) => {
                                                e.stopPropagation();
                                            }}
                                        >
                                            <ul className='m-0 p-0 list-none pl-[39px]'>
                                                {category.links.map((link) => (
                                                    <li key={link.to} className='mb-4'>
                                                        <Link to={link.to} className='text-white text-[13px]'>
                                                            {link.label}
                                                        </Link>
                                                    </li>
                                                ))}
                                            </ul>
                                        </AccordionTab>
                                    ))}
                                </Accordion>
                            </div>
                            <div className='flex items-center justify-between gap-3 mt-auto'>
                                <div className='cursor-pointer flex-1' onClick={() => navigate('/profile')}>
                                    <div className='flex items-center gap-3'>
                                        <div className='rounded-full w-[42px] h-[42px] overflow-hidden flex-shrink-0'>
                                            <div className='w-full h-full bg-theme1-pale flex items-center justify-center'>
                                                <div className='text-theme1-primary font-bold text-base'>
                                                    {initials}
                                                </div>
                                            </div>
                                        </div>
                                        <div className='text-base font-bold'>{user.name}</div>
                                    </div>
                                </div>
                                <div className='flex-shrink-0' onClick={() => navigate('/logout')}>
                                    <IconLogout className='stroke-white w-6 h-6 fill-none' />
                                </div>
                            </div>
                            <div className='flex items-center gap-3 mt-4'>
                                <Link to='/privacy-policy' className='text-white text-xs'>
                                    Privacy Policy
                                </Link>
                                <span className='inline-block w-[5px] h-[5px] bg-white rounded-full'></span>
                                <Link to='/terms-and-conditions' className='text-white text-xs'>
                                    Terms and conditions
                                </Link>
                            </div>
                        </div>
                    </div>
                )}
            ></Sidebar>
        </>
    );
};

export default MenuSidebar;
